import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { QuillEditor } from "../../utils/quill-editor";
import { TextField, Switch, FormControlLabel, Box, Typography } from "@mui/material";
import { DocumentManagerV6 } from "../../dashboard/Media/DocumentManagerV6";
import { DirectImageUploadButton } from "../../dashboard/Media/DirectImageUploadButton";

export default function OfferCoverPage({ offer, formik, onAutoSave }) {
  const { t } = useTranslation();
  const [coverPageActive, setCoverPageActive] = useState(offer?.cover_page_active || false);
  const [coverPageTitle, setCoverPageTitle] = useState(offer?.cover_page_title || "");
  const [coverPageDescription, setCoverPageDescription] = useState(offer?.cover_page_description || "");
  const [coverPageImageId, setCoverPageImageId] = useState(offer?.cover_page_image_id || null);
  const [uploadRevision, setUploadRevision] = useState(0);

  useEffect(() => {
    if (offer) {
      setCoverPageActive(offer.cover_page_active || false);
      setCoverPageTitle(offer.cover_page_title || "");
      setCoverPageDescription(offer.cover_page_description || "");
      setCoverPageImageId(offer.cover_page_image_id || null);
    }
  }, [offer]);

  const handleActiveChange = (event) => {
    const newValue = event.target.checked;
    setCoverPageActive(newValue);
    formik.setFieldValue("cover_page_active", newValue);
    setTimeout(() => {
      onAutoSave();
    }, 500);
  };

  const handleTitleChange = (event) => {
    const newValue = event.target.value;
    setCoverPageTitle(newValue);
    formik.setFieldValue("cover_page_title", newValue);
    setTimeout(() => {
      onAutoSave();
    }, 2000);
  };

  const handleDescriptionChange = (value) => {
    setCoverPageDescription(value);
    formik.setFieldValue("cover_page_description", value);
    setTimeout(() => {
      onAutoSave();
    }, 2000);
  };

  const handleDocumentsChange = (documents) => {
    const first = Array.isArray(documents) ? documents[0] : null;
    const nextId = first?.id || null;
    if (nextId !== coverPageImageId) {
      setCoverPageImageId(nextId);
      formik.setFieldValue("cover_page_image_id", nextId);
      setTimeout(() => {
        onAutoSave();
      }, 500);
    }
  };

  return (
    <div className="bg-white shadow-sm rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {t("offers.workspace.cover.title")}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {t("offers.workspace.cover.subtitle")}
            </p>
          </div>
          <FormControlLabel
            control={
              <Switch
                checked={coverPageActive}
                onChange={handleActiveChange}
                color="primary"
              />
            }
            label={
              coverPageActive
                ? t("offers.workspace.cover.active")
                : t("offers.workspace.cover.inactive")
            }
          />
        </div>
      </div>

      {coverPageActive ? (
        <div className="px-6 py-6 space-y-6">
          <div>
            <TextField
              label={t("offers.workspace.cover.field_title")}
              variant="outlined"
              fullWidth
              value={coverPageTitle}
              onChange={handleTitleChange}
              placeholder={t("offers.workspace.cover.title_placeholder")}
              helperText={t("offers.workspace.cover.title_hint")}
            />
          </div>

          <div>
            <Typography variant="subtitle2" className="mb-2">
              {t("offers.workspace.cover.description")}
            </Typography>
            <QuillEditor
              name="cover_page_description"
              value={coverPageDescription}
              onChange={handleDescriptionChange}
              placeholder={t("offers.workspace.cover.description_placeholder")}
              rows={8}
            />
          </div>

          <div>
            <Typography variant="subtitle2" className="mb-2">
              {t("offers.workspace.cover.image")}
            </Typography>
            <Box className="border border-gray-300 rounded-lg p-4 bg-gray-50 space-y-3">
              {offer?.id ? (
                <DirectImageUploadButton
                  module="offer"
                  moduleParam={String(offer.id)}
                  size="large"
                  onUploadComplete={(docs) => {
                    setUploadRevision((n) => n + 1);
                    if (docs?.length) {
                      handleDocumentsChange(docs);
                    }
                  }}
                />
              ) : null}
              <DocumentManagerV6
                key={`offer-cover-${offer?.id}-${uploadRevision}`}
                module="offer"
                moduleParam={offer?.id}
                allowMultiple={false}
                onDocumentsChange={handleDocumentsChange}
                mode="inline"
                showUpload={true}
                showExisting={true}
                allowDelete={true}
                allowEdit={true}
                autoLoad
                disabled={!offer?.id}
              />
              {coverPageImageId && offer?.cover_page_image ? (
                <Box className="mt-4 p-3 bg-blue-50 rounded border border-blue-200">
                  <Typography variant="caption" className="text-gray-700 font-medium">
                    {t("offers.workspace.cover.selected_image", {
                      title: offer.cover_page_image.title || t("offers.workspace.cover.no_image_title"),
                    })}
                  </Typography>
                  <Typography variant="caption" className="text-gray-500 block mt-1">
                    {t("offers.workspace.cover.image_first_hint")}
                  </Typography>
                </Box>
              ) : null}
            </Box>
            <Typography variant="caption" className="text-gray-500 mt-2 block">
              {t("offers.workspace.cover.image_pdf_hint")}
            </Typography>
          </div>
        </div>
      ) : (
        <div className="px-6 py-6">
          <Typography variant="body2" className="text-gray-500">
            {t("offers.workspace.cover.inactive_hint")}
          </Typography>
        </div>
      )}
    </div>
  );
}
